import React, { useState, useEffect } from "react";
import { getRequest, postRequest } from "../../services";
import { message } from 'antd';

const SurvayParent = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [surveyResponses, setSurveyResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const auth = JSON.parse(localStorage.getItem("user"));

  const headings = ["Survey Name", "Type", "Objective", "Questions Count", "Actions"];

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await getRequest('/api/Survey/survey-by-audience?type=parent');
      setSurveys(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching surveys:', err);
      setError('Failed to fetch surveys. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewQuestions = (survey) => {
    setSelectedSurvey(survey);
    setShowModal(true);
    // Reset responses when opening a new survey
    setSurveyResponses({});
  };

  const handleCloseModal = () => {
    setSelectedSurvey(null);
    setShowModal(false);
    setSurveyResponses({});
  };

  const handleResponseChange = (questionId, selectedOption) => {
    setSurveyResponses(prev => ({
      ...prev,
      [questionId]: selectedOption
    }));
  };

  const handleSubmitSurvey = async () => {
    if (!selectedSurvey) return;

    // Check if all questions are answered
    const unansweredQuestions = selectedSurvey.questions?.filter(
      question => !surveyResponses[question.id]
    );

    if (unansweredQuestions && unansweredQuestions.length > 0) {
      message.warning(`Please answer all questions. You have ${unansweredQuestions.length} unanswered question(s).`);
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        surveyId: selectedSurvey.id,
        userId: auth.userId,
        questionResponses: selectedSurvey.questions?.map(question => ({
          questionId: question.id,
          selectedOption: surveyResponses[question.id]
        })) || []
      };

      console.log('Submitting survey payload:', payload);
      
      const response = await postRequest('/api/Survey/response', payload);
      
      if (response.status === 200 || response.status === 201) {
        message.success("Survey submitted successfully!");
        handleCloseModal();
      } else {
        throw new Error('Failed to submit survey');
      }
    } catch (err) {
      console.error('Error submitting survey:', err);
      message.error('Failed to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {error}
        <button className="btn btn-outline-danger ms-3" onClick={fetchSurveys}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="row px-3 observationContent">
      <div className="col-md-12 px-3 py-3">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3>Parent Surveys</h3>
          <button 
            className="btn btn-outline-primary" 
            onClick={fetchSurveys}
          >
            Refresh
          </button>
        </div>

        {surveys.length === 0 ? (
          <div className="alert alert-info" role="alert">
            No surveys available for parents at the moment.
          </div>
        ) : (
          <div className="container-table100">
            <div className="wrap-table100">
              <div className="table100 ver5 m-b-110">
                <div className="table100-head">
                  <table>
                    <thead>
                      <tr className="row100 head">
                        {headings.map((head, index) => (
                          <th
                            className={`cell100 column${index + 1} ${
                              index === headings.length - 1 && "text-align-end"
                            }`}
                            key={index}
                          >
                            {head}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  </table>
                </div>

                <div className="table100-body">
                  <table>
                    <tbody>
                      {surveys.map((survey, index) => (
                        <tr className="row100 body" key={survey.id}>
                          <td className="cell100 column1">
                            <strong>{survey.surveyName}</strong>
                          </td>
                          <td className="cell100 column2">
                            <span className="badge bg-primary">{survey.surveyType}</span>
                          </td>
                          <td className="cell100 column3">
                            <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {survey.objective}
                            </div>
                          </td>
                          <td className="cell100 column4">
                            <span className="badge bg-secondary">{survey.questions?.length || 0}</span>
                          </td>
                          <td className="cell100 column5">
                            <div className="d-flex justify-content-end">
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => handleViewQuestions(survey)}
                              >
                                View Questions
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Survey Questions Modal */}
      {showModal && selectedSurvey && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedSurvey.surveyName}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Survey Info Card */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h6 className="card-title">Objective:</h6>
                    <p className="card-text">{selectedSurvey.objective}</p>
                    <div className="row">
                      <div className="col-md-3">
                        <strong>Type:</strong> {selectedSurvey.surveyType}
                      </div>
                      <div className="col-md-3">
                        <strong>Audience:</strong> {selectedSurvey.audience}
                      </div>
                      <div className="col-md-3">
                        <strong>Campus ID:</strong> {selectedSurvey.campusId}
                      </div>
                      <div className="col-md-3">
                        <strong>Questions:</strong> {selectedSurvey.questions?.length || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Questions Section */}
                <div>                  
                  {selectedSurvey.questions?.map((question, index) => (
                    <div key={question.id} className="mb-4">
                      <div className="d-flex align-items-start gap-4">
                        <div className="flex-grow-1">
                          <span className="me-2">{index + 1}.</span>
                          {question.questionText}
                        </div>
                        <div className="d-flex gap-3">
                          {question.options?.map((option, optionIndex) => (
                            <div key={optionIndex} className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name={`question-${question.id}`}
                                id={`question-${question.id}-${optionIndex}`}
                                value={option}
                                checked={surveyResponses[question.id] === option}
                                onChange={() => handleResponseChange(question.id, option)}
                              />
                              <label className="form-check-label" htmlFor={`question-${question.id}-${optionIndex}`}>
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleSubmitSurvey}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Survey'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default SurvayParent;
